import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close, History, Search } from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface PreviousAudience {
  id: string;
  listId: string;
  listName: string;
  audienceName: string;
  filters: any;
  segment: any[];
  daysBack: number | null;
  score: string[] | null;
  audienceConfig: any;
  filterSummary: string[];
  createdDate: string;
  updatedDate: string | null;
}

interface PreviousAudiencesDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  currentListId: string;
  onSelectAudience: (audience: PreviousAudience) => void;
}

const PreviousAudiencesDialog: React.FC<PreviousAudiencesDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  currentListId,
  onSelectAudience,
}) => {
  const [loading, setLoading] = useState(false);
  const [audiences, setAudiences] = useState<PreviousAudience[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadAudiences();
    }
  }, [open, companyId]);

  const loadAudiences = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/audience/company-audiences`,
        {
          params: {
            companyId,
            excludeListId: currentListId,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAudiences(response.data.audiences || []);
    } catch (err: any) {
      console.error("Failed to load audiences:", err);
      setError(err.response?.data?.error || "Failed to load audiences");
    } finally {
      setLoading(false);
    }
  };

  const filteredAudiences = audiences
    .filter((audience) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        audience.audienceName.toLowerCase().includes(query) ||
        audience.listName.toLowerCase().includes(query) ||
        audience.filterSummary.some((s) => s.toLowerCase().includes(query))
      );
    })
    .slice(0, 20);

  const handleSelect = (audience: PreviousAudience) => {
    onSelectAudience(audience);
    onClose();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "80vh" } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <History color="primary" />
          <Typography variant="h6">Load Filters from Previous Audience</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by audience or list name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2, textAlign: "center" }}>
            {error}
          </Typography>
        ) : filteredAudiences.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 4, textAlign: "center" }}
          >
            {searchQuery
              ? "No audiences match your search"
              : "No previous audiences found"}
          </Typography>
        ) : (
          <List sx={{ pt: 0 }}>
            {filteredAudiences.map((audience) => (
              <Paper
                key={audience.id}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  overflow: "hidden",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemButton onClick={() => handleSelect(audience)}>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="medium">
                          {audience.audienceName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(
                            audience.updatedDate || audience.createdDate
                          )}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          From: {audience.listName}
                        </Typography>
                        {audience.filterSummary.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ gap: 0.5 }}
                          >
                            {audience.filterSummary.slice(0, 6).map((filter, idx) => (
                              <Chip
                                key={idx}
                                label={filter}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                            ))}
                            {audience.filterSummary.length > 6 && (
                              <Chip
                                label={`+${audience.filterSummary.length - 6} more`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                            )}
                          </Stack>
                        )}
                        {audience.filterSummary.length === 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontStyle="italic"
                          >
                            No filters configured
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </Paper>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviousAudiencesDialog;
